"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const stories = [{
  id: 1,
  title: "Estate Transformation",
  category: "Hardscaping",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_estate_transformation.png",
  ratio: 16 / 9,
  span: "md:col-span-7",
  description: "A comprehensive architectural overhaul of a 5-acre estate, featuring custom stone masonry, tiered retaining walls, and a signature oak-lined driveway."
}, {
  id: 2,
  title: "Modern Oasis",
  category: "Patio Design",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_modern_oasis.png",
  ratio: 4 / 5,
  span: "md:col-span-5",
  description: "Sleek, geometric patio installation utilizing premium pavers and integrated lighting to create a seamless transition between indoor and outdoor living."
}, {
  id: 3,
  title: "The Heritage Garden",
  category: "Softscaping",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_heritage_garden.png",
  ratio: 4 / 5,
  span: "md:col-span-5",
  description: "Curated botanical selection focused on native species and seasonal color, designed to provide year-round visual interest and ecological harmony."
}, {
  id: 4,
  title: "Lakeside Retreat",
  category: "Full Renovation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/landscaping_lakeside_retreat.png",
  ratio: 16 / 9,
  span: "md:col-span-7",
  description: "A complex shoreline restoration project that combined erosion control with luxury entertainment spaces, including a custom fire pit and dock access."
}];
export default function ImpactStories() {
  return <section data-section-id="223734" id="masterpiece-gallery" className="bg-background text-foreground py-24 md:py-32 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 max-w-2xl">
          <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tight mb-6 uppercase text-primary">
            Masterpiece Gallery
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
            Elite outdoor environments crafted with precision. Explore our portfolio 
            of rugged luxury landscapes that redefine architectural excellence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {stories.map((story, i) => <div key={story.id} data-index={i} className={story.span}>
              <Dialog>
                <DialogTrigger className="w-full h-full text-left outline-none group block relative overflow-hidden bg-muted rounded-lg shadow-md">
                  <AspectRatio ratio={story.ratio} className="w-full h-full">
                    <img src={story.image} alt={story.title} className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 bg-primary/80 backdrop-blur-sm">
                      <div className="text-center transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 ease-out px-6">
                        <span className="text-secondary font-mono text-xs md:text-sm uppercase tracking-widest mb-2 block opacity-90">
                          {story.category}
                        </span>
                        <h3 className="text-2xl md:text-4xl font-default font-bold text-primary-foreground tracking-tight mb-4">
                          {story.title}
                        </h3>
                        <span className="inline-block px-6 py-2 border border-secondary text-secondary text-xs uppercase tracking-widest font-bold rounded-md">
                          View Details
                        </span>
                      </div>
                    </div>
                  </AspectRatio>
                </DialogTrigger>
                <DialogContent className="max-w-5xl p-0 overflow-hidden border-border bg-card rounded-lg shadow-2xl gap-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
                    <div className="relative h-64 md:h-full min-h-[300px]">
                      <img src={story.image} alt={story.title} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="p-8 md:p-16 flex flex-col justify-center bg-card">
                      <span className="text-secondary font-mono text-sm uppercase tracking-widest mb-6 block">
                        {story.category}
                      </span>
                      <DialogTitle className="text-3xl md:text-5xl font-default font-bold mb-6 tracking-tight text-primary">
                        {story.title}
                      </DialogTitle>
                      <DialogDescription className="text-muted-foreground text-base md:text-lg mb-10 leading-relaxed font-sans">
                        {story.description}
                      </DialogDescription>
                      <Button asChild size="lg" className="w-fit rounded-md uppercase tracking-wider font-bold group bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                        <Link to="/HomePage">
                          View Case Study
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>

        <div className="mt-16 md:mt-24 flex justify-center">
          <Button asChild variant="outline" size="lg" className="rounded-md border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold uppercase tracking-widest px-12 py-8 transition-all duration-300">
            <Link to="/HomePage">View All Projects</Link>
          </Button>
        </div>
      </div>
    </section>;
}